import { vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { isAlbum } from 'reactApp/modules/albums/albums.types';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { PastSubscriptionsSelectors } from 'reactApp/modules/pastSubscriptions/pastSubscriptions.selectors';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { getVigboBreadcrumbsIconSettings } from 'reactApp/modules/vigbo/vigbo.helpers';
import type { VigboLandingProduct } from 'reactApp/modules/vigbo/vigbo.types';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import type { BreadcrumbsListItem } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { vigboCrumb } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer.data';
import { isMonthPeriod, isYearPeriod } from 'reactApp/utils/Period';
import { createSelector } from 'reselect';

const getVigboState = (state: RootState) => state.vigbo;

export const getVigboList = (state: RootState) => getVigboState(state).list;

export const getVigboLoadingState = (state: RootState) => {
    const { isLoading, isLoaded } = getVigboState(state);
    return { isLoaded, isLoading };
};

export const getVigboLinkState = (state: RootState) => getVigboState(state).linkState;

export const getVigboItemById = createSelector(
    (_: RootState, id: string) => id,
    getVigboList,
    (id, list) => {
        return list[id];
    }
);

export const getVigboAlbums = createSelector(getVigboList, (list) => {
    return Object.values(list).filter(isAlbum);
});

export const getVigboAlbumById = createSelector(
    (_: RootState, id: string) => id,
    getVigboList,
    (id, list) => {
        const album = list[id];
        return isAlbum(album) ? album : undefined;
    }
);

export const getCurrentVigboAlbum = createSelector(getCurrentRouteId, getVigboList, (id, list) => {
    const album = list[id];
    return isAlbum(album) ? album : undefined;
});

export const getCurrentVigboAlbumIds = createSelector(getCurrentVigboAlbum, (album) => {
    return album?.childs || [];
});

export const getVigboAlbumIdsParams = createSelector(
    getVigboList,
    getCurrentVigboAlbum,
    (_, itemIds: string[]) => itemIds,
    (list, currentAlbum, itemIds) => {
        if (!itemIds?.length) {
            return;
        }

        /** Если пришел id альбома, то возвращаем только его */
        const itemId = itemIds[0];
        const item = list[itemId];
        if (item && isAlbum(item)) {
            return { id: itemId };
        }

        return currentAlbum ? { id: currentAlbum.id, album_list: itemIds } : undefined;
    }
);

export const hasActiveVigboTariff = (state: RootState) => {
    const vigboSubs = BillingDataSelectors.getSubscriptionsByProductIds(state, vigboTariffs);
    return vigboSubs.length > 0;
};

export const hasFinishedVigboTariff = (state: RootState) => {
    const vigboPastSubs = PastSubscriptionsSelectors.getPastSubscriptionsByProductIds(state, vigboTariffs);
    return vigboPastSubs.length > 0;
};

export const getVigboBreadcrumbs = createSelector(
    getCurrentVigboAlbum,
    getVigboLinkState,
    hasActiveVigboTariff,
    isCurrentDarkTheme,
    (album, linkState, isVigboActiveTariff, isDarkTheme) => {
        const rootCrumb = {
            ...vigboCrumb[0],
            ...getVigboBreadcrumbsIconSettings({ linkState, isVigboActiveTariff, isDarkTheme }),
        };

        const crumbs: BreadcrumbsListItem[] = [rootCrumb];

        if (album) {
            crumbs.push({
                name: album.name,
                id: album.id,
                storage: EStorageType.vigbo,
                kind: 'folder',
            });
        }

        return crumbs;
    }
);

export const getProductsForLanding = createSelector(
    (state: RootState, tariffIds: string[]) => ProductsSelectors.getAvailableProductsByListIds(state, tariffIds),
    (tariffs) => {
        const productsFromTariffs = tariffs.reduce<Product[]>((products, tariff) => [...products, ...tariff.products], []);
        const nonTrialProducts = productsFromTariffs.filter((product) => !product.hasTrial);
        const normalizedProducts = nonTrialProducts.map((product) => ({
            id: product.id,
            space: product.space,
            period: product.period,
            price: product.price,
            discountPrice: product.discountPrice,
        }));
        return normalizedProducts.reduce<{ year: VigboLandingProduct[]; month: VigboLandingProduct[] }>(
            (acc, product) => {
                if (isYearPeriod(product.period)) {
                    acc.year.push(product);
                } else if (isMonthPeriod(product.period)) {
                    acc.month.push(product);
                }

                return acc;
            },
            { year: [], month: [] }
        );
    }
);
